import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { Visibility, Edit, Search } from '@mui/icons-material';
import {
  Box, Grid, Typography, CircularProgress, TextField, Select, InputLabel, FormControl, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination,
  TableSortLabel, Chip, Tabs, Button, Tab, InputAdornment, Autocomplete, Dialog, DialogTitle,
  DialogContent, DialogActions
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import { useNavigate } from 'react-router-dom';
import { makeApiRequest } from '../../../utils/api';
import { formatDate } from '../../../helpers/timeHelper';
import OverflowTooltip from '../../../helpers/OverflowTooltip';
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from 'xlsx';

export const ListStudents = () => {
  const [selectedOption, setSelectedOption] = useState('All Locations');
  const [studentData, setStudentData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [batchList, setBatchList] = useState([]);
  const [courseList, setCourseList] = useState([]);
  const [batchId, setBatchId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [records, setRecords] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusTab, setStatusTab] = useState(0);
  const control = localStorage.getItem('control');
  const userLocation = localStorage.getItem('userLocation');
  const navigate = useNavigate();
  const statusOptions = ['Active', 'Completed', 'Resigned', 'Abscond'];

  const getStatusConfig = (status) => {
    const statusMap = {
      active: { color: '#1B5E20', bgColor: '#81B184', border: '1px solid #1B5E20', variant: 'filled' }, // Green
      completed: { color: '#0077B6', bgColor: '#90E0EF', border: '1px solid #0077B6', variant: 'filled' }, // Blue
      resigned: { color: '#D32F2F', bgColor: '#fac8c7', border: '1px solid #D32F2F', variant: 'filled' }, // Red
      abscond: { color: '#FFA000', bgColor: '#ffeeba', border: '1px solid #FFA000', variant: 'filled' }, // Yellow
    };
    return statusMap[status.toLowerCase()] || { color: '#FFFFFF', bgColor: '#B0BEC5', border: '1px solid #78909C' };
  };

  const filterDataByStatus = (data, tabIndex) => {
    switch (tabIndex) {
      case 0: return data;
      case 1: return data.filter(student => student.status.toLowerCase() === 'active');
      case 2: return data.filter(student => student.status.toLowerCase() === 'completed');
      case 3: return data.filter(student => ['resigned', 'abscond'].includes(student.status.toLowerCase()));
      default: return data;
    }
  };

  const fetchCourseData = async () => {
    try {
      const response = await makeApiRequest('post', '/api/course/list', {
        token: localStorage.getItem('authToken'),
      });
      if (response?.status === 'success' && response.data) {
        setCourseList(response.data);
      } else {
        setCourseList([]);
      }
    } catch (err) {
      console.error("Error fetching courses:", err);
      setCourseList([]);
    }
  };

  const fetchBatchData = async () => {
    try {
      const response = await makeApiRequest('post', '/api/listBatches/active', {
        token: localStorage.getItem('authToken'),
      });
      if (response?.status === 'success' && response.data) {
        setBatchList(response.data);
      } else {
        setBatchList([]);
      }
    } catch (err) {
      console.error("Error fetching batches:", err);
      setBatchList([]);
    }
  };

  const handleStatusTabChange = (event, newValue) => {
    setStatusTab(newValue);
  };

  const handleChange = (event) => {
    setSelectedOption(event.target.value);
  };

  const fetchStudentData = async () => {
    setLoading(true);
    try { 
      const response = await makeApiRequest('post', '/api/team/list', {
        token: localStorage.getItem('authToken'),
        data: {
          location: (control === '2' || control === '4') && userLocation ? userLocation : selectedOption,
          control: 3,
          batch_id: batchId,
          course_id: courseId,
          per_page: rowsPerPage,
          page: page + 1,
          search: searchQuery,
        }
      });
      if (response?.status === 'success' && response.data) {
        setStudentData(response.data.list);
        setFilteredData(response.data.list);
        setRecords(response.data.pagination?.total);
      } else {
        setStudentData([]);
        setFilteredData([]);
      }
    } catch (err) {
      console.error("Error fetching students:", err);
      setStudentData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditBatch = (student) => {
    setSelectedStudent(student);
    const currentBatch = batchList.find(batch => batch.id === student.batch_id);
    setSelectedBatch(currentBatch || null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedStudent(null);
    setSelectedBatch(null);
  };

  const handleConfirmBatch = async () => {
    if (!selectedStudent || !selectedBatch) {
      toast.error('Please select a batch');
      return;
    }

    try {
      const response = await makeApiRequest('post', '/api/employee/updateBatch', {
        token: localStorage.getItem('authToken'),
        data: {
          userid: selectedStudent.userid,
          batch_id: selectedBatch.id,
        },
      });

      if (response?.status === 'success') {
        setStudentData((prev) =>
          prev.map((student) =>
            student.userid === selectedStudent.userid ? { ...student, batch_id: selectedBatch.id } : student
          )
        );
        setFilteredData((prev) =>
          prev.map((student) =>
            student.userid === selectedStudent.userid ? { ...student, batch_id: selectedBatch.id } : student
          )
        );
        toast.success(`Batch updated for ${selectedStudent.name}`);
        handleCloseModal();
      } else {
        toast.error('Failed to update batch');
      }
    } catch (err) {
      console.error('Error updating batch:', err);
      toast.error('Error updating batch');
    }
  };

  const handleOpenStatusModal = (row) => {
    setSelectedStudent(row);
    setSelectedStatus(row.status);
    setOpenStatusModal(true);
  };

  const handleCloseStatusModal = () => {
    setOpenStatusModal(false);
    setSelectedStudent(null);
    setSelectedStatus('');
  };

  const handleConfirmStatus = async () => {
        if (!selectedStudent || !selectedStatus) {
            toast.error('Please select a status');
            return;
        }

        const currentStatus = selectedStudent.status.toLowerCase();
        if (currentStatus === selectedStatus) {
            toast.info('No status change');
            handleCloseStatusModal();
            return;
        }

        try {
            const response = await makeApiRequest('post', '/api/employee/updateStatus', {
                token: localStorage.getItem('authToken'),
                data: {
                    id: selectedStudent.userid,
                    estatus: selectedStatus,
                },
            });

            if (response?.status === 'success') {
                setStudentData((prev) =>
                    prev.map((student) =>
                        student.userid === selectedStudent.userid ? { ...student, status: selectedStatus } : student
                    )
                );
                setFilteredData((prev) =>
                    prev.map((student) =>
                        student.userid === selectedStudent.userid ? { ...student, status: selectedStatus } : student
                    )
                );
                toast.success(`${selectedStudent.name} moved from ${currentStatus} to ${selectedStatus}`);
                handleCloseStatusModal();
            } else {
                toast.error(response?.message || 'Failed to update status');
            }
        } catch (err) {
            console.error('Error updating status:', err);
            toast.error(err.message || 'Error updating status');
        }
    };

  useEffect(() => {
    fetchBatchData();
    fetchCourseData();
  }, []);

  useEffect(() => {
    fetchStudentData();
  }, [selectedOption, batchId, courseId, page, rowsPerPage, searchQuery]);

  useEffect(() => {
    let result = studentData;
    result = filterDataByStatus(result, statusTab);
    if (searchQuery) {
      result = result.filter(student => {
        const nameMatch = student.name?.toLowerCase().includes(searchQuery.toLowerCase());
        const userIdMatch = student.userid
          ? String(student.userid).toLowerCase().includes(searchQuery.toLowerCase())
          : false;
        return nameMatch || userIdMatch;
      });
    }
    setFilteredData(result);
    if (searchQuery) setPage(0);
  }, [searchQuery, statusTab, studentData]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleView = (userid) => {
    navigate(`/view-profile`, { state: { userid: userid } });
  };

  const handleExport = async () => {
    const doc = new jsPDF();
    const tableColumn = ["Name", "Email", "DOB", "Mobile Number", "Course", "Batch", "Status"];
    const tableRows = filteredData.map(row => [
      row.name,
      row.email,
      formatDate(row.dob),
      row.mobileNum,
      courseList.find(course => course.id === row.course_id)?.course_name || "N/A",
      batchList.find(batch => batch.id === row.batch_id)?.batch_name || "N/A",
      row.status.charAt(0).toUpperCase() + row.status.slice(1)
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: { fillColor: [72, 30, 130] },
    });

    doc.save('students_list.pdf');
  };

  const handleExportExcel = async () => {
    const data = filteredData.map(row => ({
      Name: row.name,
      Email: row.email,
      DOB: formatDate(row.dob),
      "Mobile Number": row.mobileNum,
      Course: courseList.find(course => course.id === row.course_id)?.course_name || "N/A",
      Batch: batchList.find(batch => batch.id === row.batch_id)?.batch_name || "N/A",
      Status: row.status.charAt(0).toUpperCase() + row.status.slice(1)
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "students_list.xlsx");
  };

  return (
    <Box sx={{ backgroundColor: "#ECEDFF", minHeight: '100vh', position: 'relative' }}>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover={false}
        theme="light"
      />
      <Grid container sx={{ height: "auto", display: 'flex', justifyContent: "center", alignItems: "" }}>
        <Grid item xs={11} sx={{ height: "80px", display: 'flex', mb: { xs: 6, sm: 0 }, mt: { xs: 2, sm: 0 } }}>
          <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid item xs={12} sm={5} md={6}>
              <Typography variant='h4' sx={{ color: "#481E82", fontSize: { xs: '24px', sm: '34px' } }}>Student's List</Typography>
            </Grid>
            <Grid item xs={12} sm={7} md={6}>
              <Grid container spacing={0} sx={{ justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={handleExport}
                  startIcon={<PictureAsPdfIcon />}
                  sx={{
                    backgroundColor: "orange",
                    width: { xs: "100%", sm: "fit-content" },
                    marginRight: { xs: 0, sm: 2 }
                  }}>
                  Download as PDF
                </Button>
                <Button
                  variant="contained"
                  onClick={handleExportExcel}
                  startIcon={<GridOnIcon />}
                  sx={{
                    backgroundColor: "#4CAF50",
                    "&:hover": { backgroundColor: "#45A049" },
                    width: { xs: "100%", sm: "fit-content" },
                    marginTop: { xs: 1, sm: 0 }
                  }}
                >
                  Export to EXCEL
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={11} sx={{
          backgroundColor: "white", borderRadius: "5px", boxShadow: " 0px 1px 2px rgba(0, 0, 0, 0.2);"
        }}>
          <Tabs
            value={statusTab}
            onChange={handleStatusTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="student status tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 'bold',
              }
            }}
          >
            <Tab label={`All (${studentData.length})`} />
            <Tab label={`Active (${studentData.filter(s => s.status.toLowerCase() === 'active').length})`} />
            <Tab label={`Completed (${studentData.filter(s => s.status.toLowerCase() === 'completed').length})`} />
            <Tab label={`Others (${studentData.filter(s => ['resigned', 'abscond'].includes(s.status.toLowerCase())).length})`} />
          </Tabs>
        </Grid>
        <Grid item xs={11} >
          <Grid container>
            <Grid item xs={12} sx={{ height: "auto", mt: 1, backgroundColor: "white", display: "flex", alignItems: "center", py: 3, px: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    id="search-box"
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder='Search'
                    sx={{ width: "100%" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    size="small"
                    options={batchList}
                    getOptionLabel={(option) => option.batch_name}
                    value={batchList.find((b) => b.id === batchId) || null}
                    onChange={(event, newValue) => setBatchId(newValue ? newValue.id : "")}
                    renderInput={(params) => <TextField {...params} label="Batch" fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    size="small"
                    options={courseList}
                    getOptionLabel={(option) => option.course_name}
                    value={courseList.find((c) => c.id === courseId) || null}
                    onChange={(event, newValue) => setCourseId(newValue ? newValue.id : "")}
                    renderInput={(params) => <TextField {...params} label="Course" fullWidth />}
                  />
                </Grid>
                {control == 1 && (
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="dropdown-label">Select Location</InputLabel>
                      <Select
                        labelId="dropdown-label"
                        id="dropdown"
                        value={selectedOption}
                        label="Select Location"
                        onChange={handleChange}
                      >
                        <MenuItem value="All Locations">All Locations</MenuItem>
                        <MenuItem value="Chennai">Chennai</MenuItem>
                        <MenuItem value="Trichy">Trichy</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ backgroundColor: "white", marginTop: "", padding: "" }}>
              <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#ECEDFF" }}>
                      <TableCell>
                        <TableSortLabel active={orderBy === "name"} direction={orderBy === "name" ? order : "asc"} onClick={() => handleSort("name")}>
                          <strong>Name</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel active={orderBy === "email"} direction={orderBy === "email" ? order : "asc"} onClick={() => handleSort("email")}>
                          <strong>Email</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel active={orderBy === "dob"} direction={orderBy === "dob" ? order : "asc"} onClick={() => handleSort("dob")}>
                          <strong>DOB</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel active={orderBy === "mobileNum"} direction={orderBy === "mobileNum" ? order : "asc"} onClick={() => handleSort("mobileNum")}>
                          <strong>Mobile Number</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell><strong>Course</strong></TableCell>
                      <TableCell><strong>Batch</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Action</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {row.name}
                          <br />
                          <Typography variant="body2" sx={{ fontSize: '13px', color: 'gray' }}>
                            {row.location} | {row.userid}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{formatDate(row.dob)}</TableCell>
                        <TableCell>{row.mobileNum}</TableCell>
                        <TableCell>
                          <OverflowTooltip>
                            {row.course_id && courseList.length
                              ? courseList.find(course => course.id === row.course_id)?.course_name || "N/A"
                              : "N/A"}
                          </OverflowTooltip>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title="Edit Batch">
                              <IconButton onClick={() => handleEditBatch(row)} aria-label="edit" sx={{ color: "#1976D2" }}>
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <OverflowTooltip>
                              {row.batch_id && batchList.length
                                ? batchList.find(batch => batch.id === row.batch_id)?.batch_name || "N/A"
                                : "N/A"}
                            </OverflowTooltip>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                            size="small"
                            onClick={() => handleOpenStatusModal(row)}
                            sx={{
                              minWidth: '80px',
                              backgroundColor: getStatusConfig(row.status).bgColor,
                              color: getStatusConfig(row.status).color,
                              padding: '4px 10px',
                              borderRadius: '4px',
                              height: '28px',
                              border: getStatusConfig(row.status).border,
                              width: '110px',
                              cursor: 'pointer',
                              fontWeight: 'bold',
                              '&:hover': {
                                backgroundColor: getStatusConfig(row.status).bgColor,
                                opacity: 0.9,
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View">
                            <IconButton onClick={() => handleView(row.userid)} aria-label="view" sx={{ color: "#1976D2" }}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={records}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {loading && (
        <Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress size={40} thickness={4} sx={{ color: '#481E82' }} />
          </Box>
        </Typography>
      )}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Edit Batch</DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <Autocomplete
            size="medium"
            options={batchList}
            getOptionLabel={(option) => option.batch_name}
            value={selectedBatch}
            onChange={(event, newValue) => setSelectedBatch(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Batch"
                fullWidth
                variant="outlined"
                sx={{ fontSize: '1rem' }}
              />
            )}
            sx={{ mt: 3 }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleCloseModal}
            variant="outlined"
            size="large"
            sx={{ minWidth: '120px', fontSize: '1rem' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBatch}
            variant="contained"
            color="primary"
            size="large"
            sx={{ minWidth: '120px', fontSize: '1rem' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openStatusModal}
        onClose={handleCloseStatusModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Change Status</DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <FormControl fullWidth size="medium" sx={{ mt: 3 }}>
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {statusOptions.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleCloseStatusModal}
            variant="outlined"
            size="large"
            sx={{ minWidth: '120px', fontSize: '1rem' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStatus}
            variant="contained"
            color="primary"
            size="large"
            sx={{ minWidth: '120px', fontSize: '1rem' }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};